// The intended precedence order for locale is:
// 1. Test URL parameter
// 2. The locale set by calling setLocale
// 3. Nova's locale as determined by getLanguage. This will default to navigator.language
import { ltjsEnvironment } from '@sas-dvr/ltjs-commons';
import { BehaviorSubject } from 'rxjs';
import { useMemo } from 'react';
import { useBehaviorSubjectValue } from './utils';
import { getTextDirFromLocale, isValidLocale } from '../utils/rtl';
import { getUriParameter, setLocale as setLTJSLocale } from '@sas-dvr/ltjs-commons/ltjsUtil';
import { getLanguage, onLanguageChange, setLanguage, waitForInit } from '@sas/nova-core/i18n';
const localeUriParameter = getUriParameter('VASDK_LOCALE');
const formatterLocaleUriParameter = getUriParameter('VASDK_FORMATTERLOCALE');
let userSetLocale = null;
export const locale$ = new BehaviorSubject(localeUriParameter || window.navigator.language);
export const formatterLocale$ = new BehaviorSubject(formatterLocaleUriParameter || null);
waitForInit().then(() => {
    locale$.next(localeUriParameter || userSetLocale || getLanguage());
    if (localeUriParameter) {
        setLanguage(localeUriParameter);
    }
});
ltjsEnvironment.runWhenInitialized(() => {
    locale$.subscribe((value) => setLTJSLocale(value));
});
onLanguageChange((language) => locale$.next(localeUriParameter || userSetLocale || language));
/**
 * @public
 */
export function setLocale(value) {
    if (!localeUriParameter) {
        userSetLocale = value;
        setLanguage(value || window.navigator.language);
    }
}
export function getLocale() {
    return locale$.getValue();
}
export function useLocale() {
    return useBehaviorSubjectValue(locale$);
}
/**
 * @public
 */
export function setFormatterLocale(value) {
    if (!formatterLocaleUriParameter) {
        formatterLocale$.next(value);
    }
}
export function getFormatterLocale() {
    return formatterLocale$.getValue();
}
export function useFormatterLocale() {
    return useBehaviorSubjectValue(formatterLocale$);
}
export function getLocaleTextDir() {
    return getTextDir(locale$.value);
}
export function useLocaleTextDir() {
    const locale = useLocale();
    return useMemo(() => getTextDir(locale), [locale]);
}
function getTextDir(locale) {
    // NOTE: this is the check that nova uses.
    if (locale.endsWith('-rtl')) {
        return 'rtl';
    }
    if (!isValidLocale(locale)) {
        return 'ltr';
    }
    return getTextDirFromLocale(new Intl.Locale(locale));
}
